import React, { useRef } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { getConnectProvider } from "../../agent/connectProviders";
import { openMockConnectPopup } from "../../agent/mockConnectPopup";

// In-chat integration connect.
//
// The provider registry (agent/connectProviders.js) reuses the SAME connect
// mechanism the settings pages use, so this card performs the real flow
// without navigating away from the conversation.
//
// The scripted demo popup — which reports "connected" after ~2.6s without
// contacting any provider — now survives only behind an explicit
// sg_copilot_mock_oauth = "1" flag. It used to be the DEFAULT, which meant the
// agent was told an integration was live when nothing had been connected.
const MOCK_OAUTH_FLAG = "sg_copilot_mock_oauth";

const isMockForced = () => {
  try {
    return localStorage.getItem(MOCK_OAUTH_FLAG) === "1";
  } catch (e) {
    return false;
  }
};

const ConnectIntegrationBlock = observer(({ block, superseded = false }) => {
  const busyRef = useRef(false);
  const provider = getConnectProvider(block.provider);
  const label = block.label || `Connect ${provider ? provider.label : block.provider}`;

  // Unlatch the button without telling the agent anything. The server turns
  // EVERY action result into a full LLM turn, so reporting a non-event makes
  // the copilot narrate something that has not happened yet.
  const unlatch = () => {
    busyRef.current = false;
  };

  const reportResult = (status, data) => {
    unlatch();
    copilotStore.sendActionResult(block.id, block.actionId, status, data);
  };

  const handleConnect = (choice = null) => {
    if (block.status === "connected" || copilotStore.streaming || busyRef.current) {
      return;
    }
    busyRef.current = true;

    // A provider with more than one route in (inbox: Gmail or Outlook) passes
    // the picked one; everything else uses its single connect().
    const runner = choice || provider;

    if (isMockForced() || !provider || !provider.isConfigured()) {
      // No provider wired for this environment (missing OAuth URL in the build
      // config) — fall back to the scripted popup rather than a dead button.
      openMockConnectPopup({
        label: provider ? provider.label : block.provider,
        iconUrl: provider ? new URL(provider.icon, window.location.origin).href : "",
        onDone: () => reportResult("success", { mock: true })
      });
      return;
    }

    try {
      runner.connect(result => {
        // A deferred hand-off is NOT an outcome. LinkedIn opens a multi-step
        // popup and reports this the instant it opens; the OAuth providers do
        // the same when their popup is blocked or closed early. Reporting it
        // used to fire an ACTION_RESULT, which the server turns into a turn --
        // so clicking Connect produced a background reply about a connection
        // the user had not made yet, while the popup was still on screen.
        // Just unlatch: the agent re-checks when the user next says something.
        if (result && result.deferred) {
          unlatch();
          return;
        }
        reportResult("success");
      });
    } catch (e) {
      // Never leave the button latched on a throw — busyRef is only cleared by
      // reportResult, so without this the card is dead until a page reload.
      reportResult("failed", { error: String(e && e.message) });
    }
  };

  const connected = block.status === "connected";
  // A newer step took over: an unconnected card up the thread is just noise.
  if (superseded && !connected) return null;
  const choices = (provider && provider.choices) || [];

  return (
    <div className={`copilotConnect status-${block.status}`}>
      <div className="copilotConnect__icon">
        {provider && <img src={provider.icon} alt={`Connect ${provider.label}`} />}
      </div>
      <div className="copilotConnect__meta">
        <div className="copilotConnect__label">{label}</div>
        {block.description && (
          <div className="copilotConnect__desc">{block.description}</div>
        )}
      </div>
      {connected ? (
        <div className="copilotConnect__connected">
          <i className="material-icons-outlined">check_circle</i>
          <span>Connected</span>
        </div>
      ) : choices.length ? (
        <div className="copilotConnect__choices">
          {choices.map(choice => (
            <button
              key={choice.key}
              type="button"
              className="copilotConnect__btn"
              disabled={copilotStore.streaming}
              onClick={() => handleConnect(choice)}
            >
              {choice.label}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          className="copilotConnect__btn"
          disabled={copilotStore.streaming}
          onClick={() => handleConnect()}
        >
          {block.status === "failed" ? "Retry connection" : label}
        </button>
      )}
    </div>
  );
});

export default ConnectIntegrationBlock;
